class MultistepStep extends HTMLElement {
  connectedCallback () {
    this.wizard = this.closest('multistep-wizard') || this.closest('multistep-wizard-captadores')

    this.addEventListener('valid', function (event) {
      this.valid = true
      if (event.detail.go) {
        this.next = event.detail.go
      }
      if (event.detail.auto) {
        this.goNext()
      }
    })
    this.addEventListener('invalid', function () {
      this.valid = false
    })
    if (this.nextButton) {
      this.nextButton.addEventListener('click', () => {
        this.goNext()
      })
    }
    if (this.prevButton) {
      this.prevButton.addEventListener('click', () => {
        this.goPrevious()
      })
    }
    this.addEventListener('input', (event) => {
      if (event.target.matches('input[type=email]')) this.ocultarErrorDeEmail()
    })
    // Se comprueba al salir del campo, no al pulsar "Siguiente", por dos razones: el visitante
    // ve el motivo antes de seguir rellenando, y para cuando pulse la respuesta ya esta cacheada,
    // asi que `goNext` no se queda esperando a la red. Eso ultimo importa en hipotecas ES, donde
    // el paso siguiente es el del telefono y se autoenfoca: tras esperar a un `fetch`, Safari en
    // iOS ya no considera el foco parte del gesto del usuario y no abre el teclado.
    this.addEventListener('focusout', (event) => {
      if (event.target.matches('input[type=email]')) this.emailAceptadoPorElServidor()
    })
  }

  static get observedAttributes () {
    return ['valid']
  }

  // Only called for the "valid"attribute due to observedAttributes
  attributeChangedCallback (_name, _oldValue, newValue) {
    if (newValue === null) {
      this.hideNext()
    } else {
      this.showNext()
    }
  }

  show ({ focus = false } = {}) {
    this.classList.add('shown')
    this._focus(focus)

    if (this.isStart || (this.wizard?.omitingStart && this.isFirst)) return
  }

  _focus(focus = false) {
    if (this.answers) {
      this.answers.dispatchEvent(new CustomEvent('show', { bubbles: true, detail: { focus } }))
    }
  }

  _blur() {
    if (this.answers) {
      this.answers.dispatchEvent(new CustomEvent('hide', { bubbles: true }))
    }
  }

  hide () {
    this.classList.remove('shown')
    this._blur()
  }

  async goNext () {
    if (!this.valid) return
    if (!await this.emailAceptadoPorElServidor()) return

    this.dispatchEvent(new CustomEvent('next', { bubbles: true }))
  }

  // Sentry #7506836311: el paso del email solo comprobaba `type=email` de HTML5, que acepta
  // tanto `pepe@gmai.com` como los dominios desechables. El visitante terminaba el asistente
  // entero, veia la thank-you page, y el Lead se caia despues en la validacion de dominio/MX:
  // sin lead, sin solicitud de documentacion, y sin nada que el usuario pudiera corregir. Se
  // pregunta al servidor por la MISMA regla que aplica el Lead antes de dejar avanzar, para
  // que el motivo aparezca donde el email todavia se puede arreglar.
  //
  // El resultado se cachea por valor, en positivo Y en negativo: volver a pulsar "Siguiente"
  // sin tocar el email repinta el motivo sin gastar otra consulta DNS.
  async emailAceptadoPorElServidor () {
    const input = this.querySelector('input[type=email]')
    if (!input || !input.value) return true

    // `emailComprobado` se marca ANTES de esperar: si salir del campo y pulsar "Siguiente"
    // caen casi a la vez, la segunda llamada reutiliza la consulta en vuelo en vez de lanzar otra.
    if (input.value !== this.emailComprobado) {
      this.emailComprobado = input.value
      this.comprobacionDelEmail = this.errorDelServidorPara(input.value)
    }
    const error = await this.comprobacionDelEmail
    if (!error) return true

    this.mostrarErrorDeEmail(error)
    return false
  }

  // FALLA ABIERTO ante cualquier problema de red: la autoridad sigue siendo la validacion del
  // Lead, y bloquear el paso porque el fetch no responde convertiria un corte de red en el
  // mismo lead perdido que esto viene a evitar.
  async errorDelServidorPara (email) {
    try {
      const response = await fetch('/simulacion/validar-email', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content,
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ email: email })
      })
      if (!response.ok) return null

      const data = await response.json()
      return data.valido ? null : data.error
    } catch (e) {
      console.error('[multistep] fallo comprobando el email:', e)
      return null
    }
  }

  // El motivo se pinta en el hueco que deja `Captadores::Campo::EmailComponent`, y no con el
  // globo nativo de `reportValidity`, porque Safari en iOS —de donde viene la mayoria de estos
  // casos— no lo muestra de forma fiable. Tampoco se marca el paso como invalido: eso cambiaria
  // el boton "Siguiente" por el pill deshabilitado, cuyo texto fijo ("Rellena el campo y acepta
  // condiciones") describe otro problema y confundiria a quien SI relleno el campo.
  mostrarErrorDeEmail (mensaje) {
    const error = this.querySelector('.rnk-WizardStep_ErrorEmail')
    if (!error) return

    error.textContent = mensaje
    error.classList.remove('hidden')
  }

  ocultarErrorDeEmail () {
    this.querySelector('.rnk-WizardStep_ErrorEmail')?.classList.add('hidden')
  }

  goPrevious () {
    this.dispatchEvent(new CustomEvent('prev', { bubbles: true }))
  }

  showNext () {
    this.position !== 'start' && this.buttonDisabled.classList.add('hidden')
    this.nextButton.classList.remove('hidden')
  }

  hideNext () {
    this.nextButton.classList.add('hidden')
    this.position !== 'start' && this.buttonDisabled.classList.remove('hidden')
  }

  get answers () {
    return this.querySelector('multistep-answers')
  }

  get isStart () {
    return this.position === 'start'
  }

  get isFirst() {
    return this.wizard?.steps?.[0] === this
  }

  get next () {
    return this.getAttribute('go')
  }

  set next (newNext) {
    this.setAttribute('go', newNext)
  }

  get position () {
    return this.getAttribute('position')
  }

  get prev () {
    return this.getAttribute('prev')
  }

  set prev (newPrev) {
    this.setAttribute('prev', newPrev)
  }

  get nextButton () {
    return this.querySelector('[go=next]')
  }

  get prevButton () {
    return this.querySelector('[go=prev]')
  }

  get buttonDisabled () {
    return this.querySelector('.button-like-nav-disabled')
  }

  get valid() {
    return this.hasAttribute('valid')
  }

  set valid(value) {
    if (value) {
      this.setAttribute('valid', '')
    } else {
      this.removeAttribute('valid')
    }
  }
}

export default MultistepStep
